<template>
  <form-modal
    ref="modal"
    :save-function="handleOk"
    :saving="saving"
    :ok-title="okTitle"
    :ok-variant="okVariant"
    :ok-only="okOnly"
    :no-close="noClose"
    :auto-close-on-ok="false"
    size="sm"
  >
    <template #title>{{ title }}</template>
    <template #default>
      <slot></slot>
    </template>
  </form-modal>
</template>

<script setup lang="ts">
import FormModal from '@/components/shared/modals/FormModal.vue';
import { ButtonVariant } from '@/model/button';
import { useForm } from 'vee-validate';
import { PropType, ref } from 'vue';

type FormValues = Record<string, unknown>;

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  okTitle: {
    type: String,
    default: 'button.save',
  },
  okVariant: {
    type: String as PropType<ButtonVariant>,
    default: ButtonVariant.PRIMARY,
  },
  okOnly: {
    type: Boolean,
    default: false,
  },
  noClose: {
    type: Boolean,
    default: false,
  },
  initialValues: {
    type: Object as PropType<FormValues>,
    default: () => ({}),
  },
  submit: {
    type: Function as PropType<(values: FormValues) => Promise<boolean>>,
    required: true,
  },
});

const modal = ref<InstanceType<typeof FormModal>>();
const saving = ref(false);

const { handleSubmit, resetForm } = useForm({
  initialValues: props.initialValues,
});

const handleOk = handleSubmit(async (values) => {
  saving.value = true;
  try {
    if (await props.submit(values)) {
      modal.value?.closeModal(true);
    }
  } finally {
    saving.value = false;
  }
});

function openModal() {
  resetForm({ values: props.initialValues });
  modal.value?.openModal();
}

defineExpose({ openModal });
</script>
